import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LinkIcon from '@mui/icons-material/Link';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import { Box, Button, ClickAwayListener, Fade, Paper, Popper, Stack, Tooltip, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import DashboardsTableProvider from "../../../contexts/TablesContext/DashboardsTableProvider";
import { WorkspaceContext } from '../../../contexts/WorkspaceContext';
import { WorkspacesContext } from "../../../contexts/WorkspacesContext";
import useBulkDashboards from '../../../hooks/useBulkDashboards';
import useRoute from "../../../hooks/useRoute";
import useWorkspace from "../../../hooks/useWorkspace";
import { deleteWorkspaceFunc, deleteWorkspaceUserMembershipFunc, getWorkspaceUserMembershipsFunc } from "../../../lib/helper";
import theme from '../../../theme';
import { WorkspaceUserMembership } from "../../../types/files";
import InvestorsIconFC from "../../atoms/InvestorsIconFC";
import ConfirmDialog from '../../modals/ConfirmDialog';
import GlobalPreferencesModal from "../../modals/investors/GlobalPreferencesModal";
import InvestorsModal from "../../modals/investors/InvestorsModal";
import InviteInvestorsModal from "../../modals/investors/InviteInvestorsModal";
import SharedWorkspaceSettings from "../../modals/investors/SharedWorkspaceSettings";
import AddCompaniesToInvestorLoaderModal from "../../modals/multi-action/AddCompaniesToInvestorLoaderModal";
import WorkspaceShareModal from "../../modals/workspaces/WorkspaceShareModal";
import { collectionKey } from "../../organisms/dashboards/DashboardsView";
import { withLDConsumer } from "launchdarkly-react-client-sdk";
import { useHistory } from "react-router-dom";

const useStyles = makeStyles((theme) => ({
    popup: {
        zIndex: 1500,
    },
    modal: {
        width: 270,
        height: 'fit-content',
        padding: '12px 8px 0px 8px',
        border: '1px solid lightgray',
        borderRadius: 12,
    },
    title: {
        fontFamily: 'Poppins',
        fontSize: '1rem',
        fontWeight: 'bold',
        color: 'black',
        marginLeft: 8,
    },
    disabledButton: {
        width: 'auto',
        height: 40,
        borderRadius: 32,
        borderColor: theme.colors.neutral['300'],
        background: theme.colors.neutral['200'],
        color: theme.colors.neutral['600'],
        fontSize: '1rem',
        fontWeight: 'bold',
        textTransform: 'none',
        "&:hover": {
            borderColor: theme.colors.neutral['200'],
            background: theme.colors.neutral['100'],
            color: theme.colors.neutral['500'],
        }
    },
    actionButton: {
        width: 'auto',
        height: 40,
        borderRadius: 32,
        borderColor: theme.colors.neutral['100'],
        background: '#E0F1F1',
        color: theme.palette.primary.main,
        fontSize: '1rem',
        fontWeight: 'bold',
        textTransform: 'none',
        transition: 'ease-in-out 300ms',
    },
    button: {
        width: '100%',
        padding: '6px 8px !important',
        fontFamily: 'Inter',
        fontSize: '0.9rem',
        fontWeight: 500,
        color: 'black',
        textTransform: 'none',
        justifyContent: 'flex-start',
        "& > .MuiButton-startIcon": {
            "& > .MuiSvgIcon-root": {
                width: 24,
                height: 24,
                color: theme.palette.primary.light,
            }
        },
    },
}));

const SharedWorkspaceActions: React.FC<{ actions?: string[], flags?: any }> = ({ actions, flags}) => {
    const history = useHistory();
    const classes = useStyles();
    const { workspace } = useContext(WorkspaceContext);
    const { setWorkspaces } = useContext(WorkspacesContext);
    const { clearBulk} = useBulkDashboards();
    const { redirectFromWorkspace } = useRoute();
    const { sharePublicly } = useWorkspace();

    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [loading, setLoading] = useState<string>('');
    const [inviteInvestorsModalOpen, setInviteInvestorsModalOpen] = useState<boolean>(false);
    const [addToInvestorModalOpen, setAddToInvestorModalOpen] = useState<boolean>(false);
    const [investorsModalOpen, setInvestorsModalOpen] = useState<boolean>(false);
    const [preferencesModalOpen, setPreferencesModalOpen] = useState<boolean>(false);
    const [sharedWorkspaceSettingsModalOpen, setSharedWorkspaceSettingsModalOpen] = useState<boolean>(false);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState<boolean>(false);
    const [shareModalOpen, setShareModalOpen] = useState<boolean>(false);
    const anchorRef = useRef<HTMLButtonElement>(null);

    const showAction = (action: string) => actions?.join(';').includes(action);

    const handleSharePublic = useCallback(async () => {
        if (!!workspace) {
            setShareModalOpen(false);
            await sharePublicly(true, workspace);
        }
    // eslint-disable-next-line
    }, [workspace]);

    const handleDelete = useCallback(async () => {
        setLoading('delete');
        const invitations = await getWorkspaceUserMembershipsFunc({workspaceId: workspace!.id});
        const invites = invitations as {items: WorkspaceUserMembership[]};

        await Promise.all(invites?.items?.map(user =>
            deleteWorkspaceUserMembershipFunc({workspaceId: workspace!.id, email: user.email})
        ));

        deleteWorkspaceFunc({ id: workspace!.id }).then(() => {
            setLoading('');
            setConfirmDeleteOpen(false);
            setWorkspaces(prev => prev.filter(ws => ws.id !== workspace!.id));
        }).finally(() => {
            redirectFromWorkspace();
        });
    }, [redirectFromWorkspace, setWorkspaces, workspace]);

    useEffect(() => {
        setLoading('');
        clearBulk(collectionKey);

        return () => {
            setLoading('');
            clearBulk(collectionKey);
        };
    // eslint-disable-next-line
    }, []);

    return (<>
        {!flags.sharedWorkspaces ? (
            <Tooltip placement="top-start" title={
                <Typography component="span" fontSize="1rem" fontWeight={600}>
                    {`To add companies and view investors,`}<br/>
                    {`please subscribe to the Pro plan `}
                    <Typography
                        component="span"
                        display="inline"
                        onClick={() => history.push('/subscriptions')}
                        sx={{
                            color: '#fff',
                            fontSize: '1rem',
                            fontWeight: 'bold',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            "&:hover": {
                                textDecoration: 'underline !important',
                            }
                        }}
                    >here</Typography>.
                </Typography>}
                componentsProps={{tooltip:{sx:{
                    display: 'flex', alignItems: 'center', borderRadius: '16px', transform: 'translate(-40px) !important',
                    backgroundColor: 'rgba(97, 97, 97)', minWidth: '300px', height: '70px',  padding: 2,
                }}}}>
                <Button variant="outlined"
                    className={classes.disabledButton}
                    endIcon={<ArrowDropDownIcon sx={{ transform: 'scale(1.25, 2.25)'}} />}
                >{`Actions`} </Button>
            </Tooltip>
        ) : (
            <Button variant="outlined"
                className={classes.actionButton}
                endIcon={<ArrowDropDownIcon sx={{ transform: 'scale(1.25, 2.25)'}} />}
                onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setModalOpen(prev => !prev);
                }} ref={anchorRef}
            >{`Actions`} </Button>
        )}
        <Popper open={modalOpen}
            anchorEl={anchorRef.current}
            className={classes.popup}
            placement="bottom-start" transition>
            {({ TransitionProps }) => (
                <Fade {...TransitionProps}>
                    <Box width="fit-content" height="fit-content">
                        <ClickAwayListener onClickAway={() => setModalOpen(false)}>
                            <Paper className={classes.modal} elevation={3} square={false}>
                                <Stack direction="column" spacing={1} alignItems="flex-start" justifyContent="center" width="100%" mb={1}>
                                    <Stack direction="column" alignItems="flex-start" justifyContent="center">
                                    {showAction('Add companies') && (
                                        <Button className={classes.button}
                                            startIcon={<AddCircleOutlineIcon />}
                                            onClick={(e: any) => {
                                                e.stopPropagation();
                                                e.preventDefault();
                                                setModalOpen(false);
                                                setAddToInvestorModalOpen(true);
                                            }} > {'Add companies'} </Button>
                                    )}
                                    {showAction('Invite investors') && (
                                        <Button className={classes.button}
                                            startIcon={<InvestorsIconFC width={24} height={24} color={ theme.palette.primary.light } />}
                                            onClick={(e: any) => {
                                                e.stopPropagation();
                                                e.preventDefault();
                                                setModalOpen(false);
                                                setInviteInvestorsModalOpen(true);
                                            }}> {'Invite investors'} </Button>
                                    )}
                                    {showAction('Copy workspace link') && (
                                        <Button className={classes.button}
                                            startIcon={<LinkIcon sx={{ width: 20, height: 20, color: theme.palette.primary.light }} />}
                                            onClick={(e: any) => {
                                                e.stopPropagation();
                                                e.preventDefault();
                                                setModalOpen(false);
                                                setShareModalOpen(true);
                                            }} > {'Copy workspace link'} </Button>
                                    )}
                                    {showAction('Investors in this workspace') && (
                                        <Button className={classes.button}
                                            startIcon={<InvestorsIconFC width={24} height={24} color={ theme.palette.primary.light } />}
                                            onClick={(e: any) => {
                                                e.stopPropagation();
                                                e.preventDefault();
                                                setModalOpen(false);
                                                setInvestorsModalOpen(true);
                                            }} > {'Investors in this workspace'} </Button>
                                    )}
                                    {showAction('Update my deal preferences') && (
                                        <Button className={classes.button}
                                            startIcon={<TuneIcon sx={{ color: "#7bd4d4" }} />}
                                            onClick={(e: any) => {
                                                e.stopPropagation();
                                                e.preventDefault();
                                                setModalOpen(false);
                                                setPreferencesModalOpen(true);
                                            }} > {'Update my deal preferences'} </Button>
                                    )}
                                    {showAction('Edit shared workspace') && (
                                        <Button className={classes.button}
                                            startIcon={<SettingsOutlinedIcon sx={{ color: "#7bd4d4" }} />}
                                            onClick={(e: any) => {
                                                e.stopPropagation();
                                                e.preventDefault();
                                                setModalOpen(false);
                                                setSharedWorkspaceSettingsModalOpen(true);
                                            }} > {'Edit shared workspace'} </Button>
                                    )}
                                    {showAction('Delete workspace') && (
                                        <Button variant="text"
                                            className={classes.button}
                                            startIcon={<DeleteOutlineIcon
                                                sx={{ color: `${theme.colors.error['500']} !important` }} />}
                                            onClick={(e: any) => {
                                                e.stopPropagation();
                                                e.preventDefault();
                                                setModalOpen(false);
                                                setConfirmDeleteOpen(true);
                                            }}
                                            sx={{ color: `${theme.colors.error['500']} !important` }}
                                        > {'Delete workspace'} </Button>
                                    )}
                                    </Stack>
                                </Stack>
                            </Paper>
                        </ClickAwayListener>
                    </Box>
                </Fade>
            )}
        </Popper>
        {addToInvestorModalOpen && (
            <DashboardsTableProvider>
                <AddCompaniesToInvestorLoaderModal
                    isOpen={addToInvestorModalOpen}
                    workspace={workspace!}
                    onClose={() => setAddToInvestorModalOpen(false)} />
            </DashboardsTableProvider>
        )}
        {inviteInvestorsModalOpen && (
            <InviteInvestorsModal
                isOpen={inviteInvestorsModalOpen}
                sharedWorkspace={workspace!}
                onClose={() => setInviteInvestorsModalOpen(false)}
            />
        )}
        {shareModalOpen &&  (
            <WorkspaceShareModal
                isOpen={shareModalOpen}
                workspace={workspace!}
                sharedWorkspace
                onShare={handleSharePublic}
                onClose={() => setShareModalOpen(false)}
            />
        )}
        {investorsModalOpen && (
            <InvestorsModal
                isOpen={investorsModalOpen}
                sharedWorkspace={workspace!}
                onClose={() => setInvestorsModalOpen(false)} />
        )}
        {preferencesModalOpen &&  (
            <GlobalPreferencesModal
                isOpen={preferencesModalOpen}
                onClose={() => setPreferencesModalOpen(false)}
            />
        )}
        {sharedWorkspaceSettingsModalOpen &&  (
            <SharedWorkspaceSettings
                isOpen={sharedWorkspaceSettingsModalOpen}
                sharedWorkspace={workspace!}
                onClose={() => setSharedWorkspaceSettingsModalOpen(false)}
            />
        )}
        <ConfirmDialog
            title="Delete confirmation"
            content="Are you sure you want to delete this investor?"
            open={confirmDeleteOpen}
            loading={loading === 'delete'}
            confirmCallback={handleDelete}
            cancelCallback={() => setConfirmDeleteOpen(false)}
        />
    </>);
}

export default withLDConsumer()(SharedWorkspaceActions);
